import type { CSSProperties } from 'react';
import { Link } from 'react-router';
import { Icon } from '../components/Icon';
import { NewsletterForm } from '../components/NewsletterForm';
import { MoonbasePrice, ProductActions, ProductCard } from '../components/ProductCard';
import { Seo } from '../components/Seo';
import { siteConfig } from '../config/site';

export function HomePage() {
  const featured = siteConfig.products[0];

  return (
    <>
      <Seo />
      <section className="hero section--grain">
        <div className="shell hero__grid">
          <div className="hero__copy">
            <span className="eyebrow eyebrow--line">Independent audio software</span>
            <h1>Shape the place between clean and transformed.</h1>
            <p>{siteConfig.brand.description}</p>
            <div className="hero__actions">
              <Link className="button button--dark button--large" to="/plugins/emberline">Discover Emberline <Icon name="arrow" /></Link>
              <Link className="button button--text" to="/plugins/emberline"><Icon name="spark" /> Start a 10-day trial</Link>
            </div>
            <div className="hero__format-line">
              <span>Included formats</span>
              {siteConfig.compatibility.formats.map((format) => <strong key={format}>{format}</strong>)}
            </div>
          </div>

          <div className="hero__visual hero__visual--emberline" style={{ '--product-accent': featured.accent } as CSSProperties}>
            <div className="hero-product-card">
              <div className="hero-product-card__topline"><span>{featured.eyebrow}</span><span>01 / 01</span></div>
              <img src={featured.artwork} alt={`${featured.name} plugin interface`} />
              <div className="hero-product-card__bottom">
                <div><small>Introducing</small><strong>{featured.name}</strong></div>
                <Link to={`/plugins/${featured.slug}`} aria-label={`View ${featured.name}`}><Icon name="arrow" /></Link>
              </div>
            </div>
            <div className="hero-orbit hero-orbit--one" aria-hidden="true" />
            <div className="hero-orbit hero-orbit--two" aria-hidden="true" />
            <div className="hero__note"><Icon name="waveform" /><div><span>Designed around</span><strong>One expressive gesture</strong></div></div>
          </div>
        </div>
      </section>

      <section className="proof-strip" aria-label="Emberline highlights">
        <div className="shell proof-strip__inner">
          <div><Icon name="cube" /><span><strong>One installer</strong>Standalone · AUv3 · VST3</span></div>
          <div><Icon name="spark" /><span><strong>10-day trial</strong>Every feature available</span></div>
          <div><Icon name="shield" /><span><strong>Perpetual license</strong>All version 1 updates</span></div>
          <div><Icon name="key" /><span><strong>Three computers</strong>Online or offline activation</span></div>
        </div>
      </section>

      <section className="section featured-listen">
        <div className="shell">
          <div className="section-heading section-heading--split">
            <div><span className="eyebrow">Introducing Emberline</span><h2>A whole signal path, experienced as one instrument.</h2></div>
            <p>Emberline makes complex movement approachable without hiding the controls that matter at mix time.</p>
          </div>
          <div className="featured-listen__grid">
            <div className="featured-listen__visual" style={{ '--product-accent': featured.accent } as CSSProperties}>
              <img src={featured.artwork} alt={`${featured.name} plugin interface`} />
              <div className="featured-listen__caption">
                <span>{featured.eyebrow}</span><strong>{featured.name}</strong><p>{featured.shortDescription}</p>
              </div>
            </div>
            <div className="featured-listen__content">
              <div className="feature-number">01</div>
              <span className="eyebrow">{featured.category}</span>
              <h3>{featured.name}</h3>
              <p>{featured.description}</p>
              <ul className="emberline-home-features">
                {featured.features.map((feature) => <li key={feature.title}><Icon name="check" /><span><strong>{feature.title}</strong>{feature.description}</span></li>)}
              </ul>
              <div className="featured-listen__buyrow">
                <div><MoonbasePrice product={featured} className="large-price" /><small className="launch-copy">Launch pricing for 30 days · then {featured.listPriceFallback}</small></div>
                <ProductActions product={featured} compact />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section plugin-index">
        <div className="shell">
          <div className="section-heading section-heading--split">
            <div><span className="eyebrow">Wavecraft instruments</span><h2>Focused tools. Deliberate choices.</h2></div>
            <Link className="section-link" to="/plugins">Product details <Icon name="arrow" /></Link>
          </div>
          <div className="product-grid product-grid--single"><ProductCard product={featured} featured /></div>
        </div>
      </section>

      <section className="section principles-section">
        <div className="shell principles-section__grid">
          <div className="principles-section__intro">
            <span className="eyebrow">Design position</span>
            <h2>Less time operating the plugin. More time hearing the decision.</h2>
            <p>Every control earns its place by making an audible relationship easier to understand.</p>
          </div>
          <div className="principle-list">
            <article><span>01</span><Icon name="sliders" /><h3>Broad musical control</h3><p>Amount coordinates the technical moves while keeping the result reversible and easy to recall.</p></article>
            <article><span>02</span><Icon name="waveform" /><h3>Signal-aware response</h3><p>Processing responds to the material, so one setting can remain expressive across different sources.</p></article>
            <article><span>03</span><Icon name="check" /><h3>Fair comparisons</h3><p>Level management and clear bypass behavior help separate a better sound from a louder one.</p></article>
          </div>
        </div>
      </section>

      <section className="section bundle-section">
        <div className="shell bundle-card bundle-card--single">
          <div className="bundle-card__art" aria-hidden="true"><img src={featured.artwork} alt="" /></div>
          <div className="bundle-card__copy">
            <span className="eyebrow">Emberline version 1</span>
            <h2>One purchase. Your sessions stay yours.</h2>
            <p>A perpetual license includes the standalone app, AUv3 and VST3, three active computers and every Emberline 1.x update.</p>
            <ul>
              <li><Icon name="check" /> Three simultaneous activations</li>
              <li><Icon name="check" /> Online and offline authorization</li>
              <li><Icon name="check" /> 30-day refund policy</li>
            </ul>
            <div className="bundle-card__actions">
              <div className="bundle-price"><MoonbasePrice product={featured} /><small>30-day launch price</small></div>
              <ProductActions product={featured} compact />
            </div>
          </div>
        </div>
      </section>

      <section className="section newsletter-section"><div className="shell"><NewsletterForm /></div></section>
    </>
  );
}
